import { useEffect, useRef } from "react";
import { Chart as ChartJS } from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";

interface ReportStatCardProps {
  icon: string;
  label: string;
  value: string;
  isDarkMode: boolean;
  iconColor?: string;
}

export function ReportStatCard({
  icon,
  label,
  value,
  isDarkMode,
  iconColor = "text-primary-500",
}: ReportStatCardProps) {
  return (
    <div
      className={`p-4 rounded-xl border ${
        isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-500/15">
          <i className={`${icon} ${iconColor} text-lg`} />
        </span>
        <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
      </div>
      <p className={`text-xl font-semibold mt-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
        {value}
      </p>
    </div>
  );
}

interface ReportChartProps {
  config: ChartConfiguration;
  height?: number;
}

export function ReportChart({ config, height = 280 }: ReportChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    chartRef.current = new ChartJS(ctx, config);

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [config]);

  return (
    <div style={{ height }} className="w-full relative">
      <canvas ref={canvasRef} />
    </div>
  );
}

export const CHART_PALETTE = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export const gridColor = (isDarkMode: boolean): string =>
  isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)";

export const tickColor = (isDarkMode: boolean): string =>
  isDarkMode ? "#E5E7EB" : "#374151";

export const reportLegend = (isDarkMode: boolean): any => ({
  position: "top",
  align: "start",
  labels: {
    usePointStyle: true,
    pointStyle: "circle",
    boxWidth: 9,
    boxHeight: 9,
    padding: 14,
    font: { size: 12, weight: "500" },
    color: isDarkMode ? "#F3F4F6" : "#1F2937",
  },
});
